import React, { useState, useRef, useLayoutEffect, memo } from 'react';
import ReactDOM from 'react-dom';
import reactCSS from 'reactcss';
import { SketchPicker, CompactPicker } from 'react-color';
import PropTypes from 'prop-types';
import { rgbToHex } from '../../packages/utils/functions';

const PICKER_HEIGHT = 320;
const PICKER_WIDTH = 220;
const OFFSET = 8;

const SimpleColorPicker = memo(
  ({ onChange, color, hexColor, keyProp, disabled, className, presetColors }) => {
    const [displayColorPicker, setDisplayColorPicker] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
    const swatchRef = useRef(null);
    const colorFormated =
      typeof color === 'string' ? color : `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;

    const handleOpenPicker = () => {
      if (disabled) return;
      setDisplayColorPicker(!displayColorPicker);
    };

    const handleClosePicker = () => {
      setDisplayColorPicker(false);
    };

    const handleChange = newColor => {
      const outputColor = hexColor ? rgbToHex(newColor.rgb) : newColor.rgb;

      onChange(outputColor);
    };

    const normalizePresetColors = () => {
      if (!presetColors) return null;
      return presetColors.map(preset => (typeof preset === 'string' ? preset : preset.color));
    };

    useLayoutEffect(() => {
      if (displayColorPicker && swatchRef.current) {
        const rect = swatchRef.current.getBoundingClientRect();
        let top = rect.top - PICKER_HEIGHT - OFFSET;
        let left = rect.left + rect.width / 2 - PICKER_WIDTH / 2;
        if (top < OFFSET) top = rect.bottom + OFFSET;
        if (left + PICKER_WIDTH > window.innerWidth - OFFSET) left = window.innerWidth - PICKER_WIDTH - OFFSET;
        if (left < OFFSET) left = OFFSET;
        setPopoverPosition({ top, left });
      }
    }, [displayColorPicker]);

    const styles = reactCSS({
      default: {
        color: {
          width: '34px',
          height: '34px',
          borderRadius: '40px',
          background: colorFormated,
          position: 'absolute',
        },
        swatch: {
          padding: '5px',
          borderRadius: '40px',
          display: 'inline-block',
          cursor: 'pointer',
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
        },
        popover: {
          position: 'fixed',
          zIndex: 1050,
          top: popoverPosition.top,
          left: popoverPosition.left,
        },
        cover: {
          position: 'fixed',
          top: '0px',
          right: '0px',
          bottom: '0px',
          left: '0px',
          zIndex: 1049,
        },
        container: {
          borderRadius: '10px',
        },
        iconPickerContainer: {
          width: '20px',
          height: '20px',
          background: '#CDCDCD',
          borderRadius: '40px',
          padding: '2px',
          position: 'absolute',
          top: '25px',
          left: '20px',
        },
        iconPicker: {
          fontSize: '16px',
        },
      },
    });

    const popoverContent =
      displayColorPicker && !disabled ? (
        <>
          <div style={styles.cover} onClick={handleClosePicker} />
          <div className="simpleColorPopover" style={styles.popover}>
            {presetColors ? (
              <CompactPicker
                color={color}
                colors={normalizePresetColors()}
                onChangeComplete={e => handleChange(e)}
              />
            ) : (
              <SketchPicker
                color={color}
                onChangeComplete={e => handleChange(e)}
              />
            )}
          </div>
        </>
      ) : null;

    return (
      <div key={keyProp} style={styles.container} className={className}>
        <div ref={swatchRef} style={styles.swatch} onClick={() => handleOpenPicker()}>
          <div style={styles.color} />
          <div style={styles.iconPickerContainer}>
            <span style={styles.iconPicker} className="material-icons-outlined">
              edit
            </span>
          </div>
        </div>
        {popoverContent && ReactDOM.createPortal(popoverContent, document.body)}
      </div>
    );
  },
);

SimpleColorPicker.defaultProps = {
  color: {
    r: '241',
    g: '112',
    b: '19',
    a: '1',
  },
  hexColor: false,
  disabled: false,
};

SimpleColorPicker.propTypes = {
  color: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      a: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      b: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      g: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      r: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  hexColor: PropTypes.bool,
  keyProp: PropTypes.any,
  disabled: PropTypes.bool,
  presetColors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string.isRequired,
        title: PropTypes.string,
      }),
    ])
  ),
};

export default SimpleColorPicker;
